import React, { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { CheckCircle2, Receipt, Home, Copy, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtIDR, paymentImg } from "@/lib/ellaApi";
import { toast } from "sonner";

/**
 * Payment instruction screen after doPay (legacy `cart-completed` parity).
 * doPay data = { PaymentMethodCategory, PaymentMethodLogo, PaymentMethod,
 *                ExpiredDate, ReferenceID, GrossAmount, GoPayDeepLink }
 */
export default function OrderSuccessPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const payment = loc.state?.payment;

  // Deep-linked/reloaded without payment data → go to the unpaid transactions tab
  useEffect(() => {
    if (!payment) nav("/account/transactions", { replace: true });
  }, [payment, nav]);
  if (!payment) return null;

  const category = payment.PaymentMethodCategory || "";
  const refLabel = category === "bank_transfer" ? "Nomor Virtual Account"
    : category === "cstore" ? "Payment Code"
    : category === "echannel" ? "Kode Pembayaran (Bill Key)"
    : category === "direct" ? "Rekening Tujuan"
    : "";
  const showRef = !!payment.ReferenceID && category !== "gopay";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Disalin!");
  };

  const openGopay = () => {
    toast.info("Ellafroze akan membuka aplikasi Gojek, pastikan aplikasi Gojek sudah terinstall");
    window.open(payment.GoPayDeepLink, "_blank");
  };

  return (
    <div className="p-4 pb-32 text-center">
      <div className="mt-6 mb-6 flex justify-center">
        <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-600" />
        </div>
      </div>
      <h1 className="font-display text-2xl font-bold text-gray-900" data-testid="order-success-title">Pesanan Berhasil Dibuat!</h1>
      <p className="text-sm text-gray-600 mt-2 max-w-xs mx-auto">Mohon selesaikan pembayaran sebelum batas waktu yang ditentukan.</p>

      <div className="mt-6 mx-auto bg-white border border-gray-100 rounded-2xl p-4 text-left space-y-2.5">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-500">Metode</span>
          <span className="flex items-center gap-2 font-semibold">
            {payment.PaymentMethodLogo && (
              <img src={paymentImg(payment.PaymentMethodLogo)} alt="" className="h-5 object-contain" onError={(e)=>{ e.target.style.display="none"; }} />
            )}
            <span data-testid="order-payment-method">{payment.PaymentMethod || "-"}</span>
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Total Bayar</span>
          <span className="font-bold text-[#D32F2F]" data-testid="order-gross-amount">{fmtIDR(payment.GrossAmount)}</span>
        </div>

        {showRef && (
          <div className="mt-1 p-3 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-500 mb-1">{refLabel}</p>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-bold text-lg break-all" data-testid="order-reference-id">{payment.ReferenceID}</span>
              <button onClick={() => copy(payment.ReferenceID)} className="text-[#D32F2F] shrink-0" data-testid="order-copy-reference">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {category === "gopay" && payment.GoPayDeepLink && (
          <Button onClick={openGopay} className="w-full h-11 rounded-full bg-[#00AA13] hover:bg-[#008d10] text-white" data-testid="order-gopay-button">
            <Wallet className="w-4 h-4 mr-2" /> Bayar dengan GoPay
          </Button>
        )}

        {payment.ExpiredDate && (
          <p className="text-xs text-gray-500 text-center">
            Bayar sebelum: <b className="text-gray-800" data-testid="order-expiry">{payment.ExpiredDate}</b>
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <Link to="/account/transactions" data-testid="order-success-view-transactions">
          <Button className="w-full h-11 btn-primary"><Receipt className="w-4 h-4 mr-2" /> Cek Status Transaksi</Button>
        </Link>
        <Link to="/" data-testid="order-success-home">
          <Button variant="outline" className="w-full h-11 rounded-full"><Home className="w-4 h-4 mr-2" /> Belanja Lagi</Button>
        </Link>
      </div>
    </div>
  );
}
